import {
  Button,
  Center,
  Divider,
  Flex,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Select,
  Switch,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { HiCheck } from '@react-icons/all-files/hi/HiCheck';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import config from '../../../config';
import { LDFlags } from '../../../constants/flags';
import {
  DealType,
  PayoffRequestStatus,
  useGenerateTestDocumentsLazyQuery,
  useRunAutoStructureCronsMutation,
} from '../../../gql/generated/graphql';
import useFlag from '../../../hooks/useFlag';
import { DealStateEnum } from '../../../utils/types/deal';
import { UserDeal } from '../utils';
import {
  allowDuplicateVinAtom,
  devAfterHoursHeldAtom,
  devDealStateAtom,
  devDealTypeAtom,
  devIs5StepAtom,
  devPayoffStatusAtom,
  devSelfServeSubmittedAtom,
  devToolbarHiddenAtom,
  documentsSignedAtom,
  isDevActiveAtom,
} from './devToolbarAtoms';
import MarketplaceDevTools from './marketplace/MarketplaceDevTools';
import useGetRandomDevVin from './useGetRandomDevVin';

type DashboardDevToolsProps = {
  userDeal: UserDeal;
  isNewDeal: boolean;
  isBooted: boolean;
  isMarketplace: boolean;
};

const DashboardDevToolbar = ({
  userDeal,
  isNewDeal,
  isBooted,
  isMarketplace,
}: DashboardDevToolsProps) => {
  const [hidden, setHidden] = useAtom(devToolbarHiddenAtom);
  const [devDealState, setDevDealState] = useAtom(devDealStateAtom);
  const [devDealType, setDevDealType] = useAtom(devDealTypeAtom);
  const [documentsSigned, setDocumentsSigned] = useAtom(documentsSignedAtom);
  const [isDevActive, setIsDevActive] = useAtom(isDevActiveAtom);
  const [devIs5Step, setDevIs5Step] = useAtom(devIs5StepAtom);
  const [devAfterHoursHeld, setDevAfterHoursHeld] = useAtom(devAfterHoursHeldAtom);
  const [devPayoffStatus, setDevPayoffStatus] = useAtom(devPayoffStatusAtom);
  const [devSelfServeSubmitted, setDevSelfServeSubmitted] = useAtom(devSelfServeSubmittedAtom);
  const [allowDuplicateVin, setAllowDuplicateVin] = useAtom(allowDuplicateVinAtom);
  const afterHoursEnabled = useFlag(LDFlags.AFTER_HOURS_SELF_SERVICE_PAYOFF);

  const [generateTestDocuments, { loading: generatingDocuments }] =
    useGenerateTestDocumentsLazyQuery({
      onCompleted: () => {
        window.location.reload();
      },
    });

  const [runAutoStructureCrons, { loading: runningCrons }] = useRunAutoStructureCronsMutation({
    onCompleted: () => {
      window.location.reload();
    },
  });

  const [getProdRandomVin, { loading: gettingRandomVin }] = useGetRandomDevVin();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setHidden((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setHidden]);

  useEffect(() => {
    if (isDevActive) return;

    if (userDeal?.state) {
      setDevDealState(userDeal.state as DealStateEnum);
    }
    if (userDeal?.type) {
      setDevDealType(userDeal.type);
    }
  }, [isDevActive, userDeal?.state, userDeal?.type]);

  if (hidden) return null;

  const isBuyout = !isNewDeal && !isBooted && !isMarketplace;

  return (
    <Center>
      <Flex gap={4} mt="10px" align="flex-start" flexWrap="wrap" justify="center">
        <VStack
          align="left"
          borderWidth={2}
          borderColor="grayBlueish"
          borderRadius="lg"
          p="10px"
          w="300px"
        >
          <Flex justify="space-between" align="center">
            <Heading textAlign="center" flex={1}>
              Dev Tools
            </Heading>
            <Tooltip label="Toggle with ⌘/Ctrl + Shift + D" placement="top">
              <Button size="xs" variant="ghost" onClick={() => setHidden(true)}>
                Hide
              </Button>
            </Tooltip>
          </Flex>
          <Text>
            Deal id:{' '}
            {userDeal?.id ? (
              <Link href={`${config.urls.appRoot}/deals/${userDeal?.id}`} target="_blank">
                {userDeal?.id}
              </Link>
            ) : (
              '--'
            )}
          </Text>
          <HStack>
            <Switch isChecked={isDevActive} onChange={(e) => setIsDevActive(e.target.checked)}>
              Dev Tools Active
            </Switch>
          </HStack>
          {isBuyout && (
            <>
              <HStack>
                <Switch
                  isChecked={documentsSigned}
                  onChange={(e) => setDocumentsSigned(e.target.checked)}
                  isDisabled={!isDevActive}
                >
                  Documents Signed
                </Switch>
              </HStack>
              <HStack>
                <Switch
                  isChecked={devIs5Step}
                  onChange={(e) => setDevIs5Step(e.target.checked)}
                  isDisabled={!isDevActive}
                >
                  PI Payoff (5-step)
                </Switch>
              </HStack>
              {afterHoursEnabled && (
                <>
                  <HStack>
                    <Switch
                      isChecked={devAfterHoursHeld}
                      onChange={(e) => setDevAfterHoursHeld(e.target.checked)}
                      isDisabled={!isDevActive}
                    >
                      After-Hours Held
                    </Switch>
                  </HStack>
                  <HStack>
                    <FormLabel width="105px">PI Status</FormLabel>
                    <Select
                      onChange={(e) =>
                        setDevPayoffStatus(
                          e.target.value ? (e.target.value as PayoffRequestStatus) : null,
                        )
                      }
                      value={devPayoffStatus ?? ''}
                      bg="white"
                      isDisabled={!isDevActive}
                      size="sm"
                    >
                      <option value="">Default</option>
                      {Object.entries(PayoffRequestStatus).map(([label, value]) => (
                        <option key={label} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                  <HStack>
                    <Switch
                      isChecked={devSelfServeSubmitted}
                      onChange={(e) => setDevSelfServeSubmitted(e.target.checked)}
                      isDisabled={!isDevActive}
                    >
                      Self-Serve Submitted
                    </Switch>
                  </HStack>
                </>
              )}
            </>
          )}
          {!isNewDeal && (
            <>
              <HStack>
                <FormLabel width="105px">Deal State</FormLabel>
                <Select
                  onChange={(e) => {
                    setDevDealState(e.target.value as DealStateEnum);
                  }}
                  value={devDealState}
                  bg="white"
                  isDisabled={!isDevActive}
                >
                  {Object.entries(DealStateEnum).map(([label, value]) => (
                    <option key={label} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </HStack>
              <HStack>
                <FormLabel width="105px">Deal Type</FormLabel>
                <Select
                  onChange={(e) => {
                    setDevDealType(e.target.value as DealType);
                  }}
                  value={devDealType}
                  bg="white"
                  isDisabled={!isDevActive}
                >
                  {Object.entries(DealType).map(([label, value]) => (
                    <option key={label} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </HStack>
            </>
          )}
          {isNewDeal && (
            <HStack>
              <Switch
                isChecked={allowDuplicateVin}
                onChange={(e) => setAllowDuplicateVin(e.target.checked)}
              >
                Allow duplicate vin
              </Switch>
            </HStack>
          )}
          <Divider my="10px" />
          {isNewDeal && (
            <Button onClick={() => getProdRandomVin()} isLoading={gettingRandomVin}>
              Get Random VIN
            </Button>
          )}
          {isBuyout && (
            <Popover placement="top">
              {({ onClose }) => (
                <>
                  <PopoverTrigger>
                    <Button isLoading={generatingDocuments} loadingText="Generating Documents...">
                      Generate Documents
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent p="10px" maxW="200px">
                    <PopoverArrow />
                    <Flex flexDir="column" alignItems="center" gap="5px">
                      Are you sure?
                      <Flex gap="5px">
                        <IconButton
                          icon={<HiCheck size="22.5px" />}
                          aria-label="confirm"
                          bg="green.500"
                          color="white"
                          _hover={{
                            bg: 'green.400',
                            boxShadow: 'md',
                          }}
                          onClick={() => {
                            onClose();
                            generateTestDocuments({
                              variables: { update_deal_id: userDeal?.id ?? '' },
                            });
                          }}
                        />
                      </Flex>
                    </Flex>
                  </PopoverContent>
                </>
              )}
            </Popover>
          )}
          <Button
            onClick={() => runAutoStructureCrons()}
            isLoading={runningCrons}
            loadingText="Running Auto Structure Crons..."
          >
            Run Auto Structure Crons
          </Button>
        </VStack>
        {isMarketplace && <MarketplaceDevTools userDeal={userDeal} />}
      </Flex>
    </Center>
  );
};

export default DashboardDevToolbar;
